/**
 * First-visit location popup.
 * Reuses the theme's native <modal-dialog> element (see global.js) for
 * open/close, focus trapping and Escape handling, and Shopify's native
 * localization form for the actual country / language switch. This file
 * only decides *when* to show the popup and remembers that a visitor has
 * already seen it.
 *
 * Editor note: <modal-dialog> relocates itself to be a direct child of
 * <body> as soon as it connects (see global.js ModalDialog). When the
 * theme editor live-reloads this section after a settings change, the OLD
 * instance is left behind under <body> (now outside the reloaded section)
 * while a brand new one is inserted and immediately relocated too — so we
 * end up with two. On every 'shopify:section:load' we drop all but the
 * newest one and (re)bind it, otherwise settings changes look like they
 * do nothing because you're still looking at the stale copy.
 *
 * Editor note: in the theme editor we only show the popup while THIS
 * section is the one selected in the sidebar (shopify:section:select /
 * shopify:section:deselect), instead of on every load. Otherwise it pops
 * back open — covering the whole canvas — every time a merchant edits any
 * OTHER section, which makes the editor unusable without exiting preview
 * first. `editorSelected` lives outside bind() so it survives this
 * section reloading while selected (e.g. while tweaking its own
 * settings), and clears automatically once the merchant selects something
 * else.
 */
(function () {
  var COOKIE_NAME = 'location_popup_seen';
  var SELECTOR = 'modal-dialog[data-location-popup]';
  var editorSelected = false;

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    var expires = '';
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
  }

  function removeStaleModals(keep) {
    document.querySelectorAll(SELECTOR).forEach(function (el) {
      if (el !== keep) el.remove();
    });
  }

  // Shopify appends "shpxid" to the URL when it redirects a visitor across
  // domains as part of a Markets localization change (e.g. after they pick
  // a country that lives on a different domain). Landing with that param
  // means the visitor just made their choice seconds ago on another one of
  // our domains — asking again here would just be repeating the same
  // question. This is an undocumented Shopify implementation detail, so
  // treat it as a best-effort heuristic: worst case it doesn't match and
  // the popup simply shows once more, nothing breaks.
  function arrivedViaMarketRedirect() {
    return /(?:^|[?&])shpxid=/.test(window.location.search);
  }

  function bind(modal) {
    if (!modal || modal.dataset.locationPopupBound || typeof modal.show !== 'function') return;
    modal.dataset.locationPopupBound = 'true';

    var rememberDays = parseInt(modal.dataset.remember, 10) || 30;
    var delay = parseInt(modal.dataset.delay, 10) || 0;

    function remember() {
      setCookie(COOKIE_NAME, '1', rememberDays);
    }

    // "Continue browsing" (and the X close button) just close the popup.
    modal.querySelectorAll('[data-location-popup-dismiss]').forEach(function (button) {
      button.addEventListener('click', function () {
        remember();
        modal.hide();
      });
    });

    // Submitting the country/language form navigates away — remember
    // before that navigation starts.
    var form = modal.querySelector('.location-popup-modal__form');
    if (form) {
      form.addEventListener('submit', remember);
    }

    // In the theme editor, only show while this section is the one
    // selected in the sidebar — see the file-level comment above.
    if (document.documentElement.classList.contains('shopify-design-mode')) {
      if (editorSelected) modal.show();
      return;
    }

    if (getCookie(COOKIE_NAME)) return;

    // Already resolved via a cross-domain market redirect — remember
    // silently on this domain too instead of showing the popup again.
    if (arrivedViaMarketRedirect()) {
      remember();
      return;
    }

    window.setTimeout(function () {
      modal.show();
    }, delay);
  }

  function refresh() {
    var modals = document.querySelectorAll(SELECTOR);
    if (!modals.length) return;
    var latest = modals[modals.length - 1];
    removeStaleModals(latest);
    bind(latest);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }

  // Re-sync whenever the theme editor live-reloads a section (e.g. after
  // a settings change).
  document.addEventListener('shopify:section:load', refresh);

  // Show/hide as the merchant selects or deselects this section in the
  // theme editor sidebar, so it's only in the way when they're actually
  // working on it.
  document.addEventListener('shopify:section:select', function (event) {
    var modal = document.querySelector(SELECTOR);
    if (!modal || event.detail.sectionId !== modal.dataset.sectionId) return;
    editorSelected = true;
    modal.show();
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    var modal = document.querySelector(SELECTOR);
    if (!modal || event.detail.sectionId !== modal.dataset.sectionId) return;
    editorSelected = false;
    modal.hide();
  });
})();
